import React, { useEffect } from 'react'
import { useAuth } from 'wasp/client/auth'
import { useNavigate } from 'react-router-dom'
import { useQuery } from 'wasp/client/operations'
import { getRetentionInsights } from 'wasp/client/operations'
// AppFrame removed: route-level layout wraps admin pages
import { Card } from '../../components/ui/card'

type Insights = {
  from: string
  to: string
  totals: {
    surveys: number
    offersAccepted: number
    cancellations: number
  }
  reasons: Array<{ reason: string; count: number }>
  offers: Array<{ type: string; accepted: number; viewed?: number; declined?: number }>
}


export function AdminRetention() {
  const { data: user } = useAuth()
  const navigate = useNavigate()
  const isAdmin = Boolean((user as any)?.isAdmin)
  useEffect(() => { if (user && !isAdmin) navigate('/') }, [user, isAdmin])

  const { data, isLoading, error } = useQuery(getRetentionInsights, { days: 30 } as any, { enabled: isAdmin })

  if ((error as any)?.message?.includes('403')) {
    return <div className="p-6 text-red-600">Access denied: admin only.</div>
  }

  return (
      <div className="space-y-6">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-martinique dark:text-white">Retention Insights</h1>
          <p className="text-martinique/70 dark:text-white/70 mt-2">Survey reasons, offer acceptance, and churn over time</p>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            <div className="h-24 bg-stone dark:bg-darkcard rounded-xl animate-pulse" />
            <div className="h-64 bg-stone dark:bg-darkcard rounded-xl animate-pulse" />
          </div>
        ) : (
          <>
            <div className="flex justify-end mb-2">
              <button
                className="px-3 py-2 rounded-md bg-warning text-ink font-medium"
                onClick={() => {
                  const d: any = data || { reasons: [], offers: [], totals: {} }
                  const rows: string[] = []
                  rows.push(['metric','value'].join(','))
                  rows.push(['surveys', d.totals?.surveys ?? 0].join(','))
                  rows.push(['offersAccepted', d.totals?.offersAccepted ?? 0].join(','))
                  rows.push(['cancellations', d.totals?.cancellations ?? 0].join(','))
                  rows.push('')
                  rows.push(['reason','count'].join(','))
                  ;(d.reasons || []).forEach((r: any) => rows.push([r.reason, r.count].join(',')))
                  rows.push('')
                  rows.push(['offerType','accepted'].join(','))
                  ;(d.offers || []).forEach((o: any) => rows.push([o.type, o.accepted].join(',')))
                  const csv = rows.join('\n')
                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                  const url = URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = `retention-insights.csv`
                  a.click()
                  URL.revokeObjectURL(url)
                }}
              >
                Export CSV
              </button>
            </div>
            <Card className="p-6 bg-sand dark:bg-darkcard border-0 rounded-xl shadow-card">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <div className="text-sm text-martinique/60 dark:text-white/60">Surveys</div>
                  <div className="text-3xl font-bold text-martinique dark:text-white">{(data as any)?.totals?.surveys ?? 0}</div>
                </div>
                <div>
                  <div className="text-sm text-martinique/60 dark:text-white/60">Offers Accepted</div>
                  <div className="text-3xl font-bold text-martinique dark:text-white">{(data as any)?.totals?.offersAccepted ?? 0}</div>
                </div>
                <div>
                  <div className="text-sm text-martinique/60 dark:text-white/60">Cancellations</div>
                  <div className="text-3xl font-bold text-martinique dark:text-white">{(data as any)?.totals?.cancellations ?? 0}</div>
                </div>
              </div>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="p-6 bg-sand dark:bg-darkcard border-0 rounded-xl shadow-card">
                <div className="text-xl font-medium text-martinique dark:text-white mb-4">Reasons Breakdown</div>
                <div className="space-y-3">
                  {((data as any)?.reasons || []).map((r: any) => (
                    <div key={r.reason} className="flex items-center justify-between">
                      <div className="text-martinique dark:text-white">{r.reason}</div>
                      <div className="font-medium text-martinique dark:text-white">{r.count}</div>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-6 bg-sand dark:bg-darkcard border-0 rounded-xl shadow-card">
                <div className="text-xl font-medium text-martinique dark:text-white mb-4">Offer Acceptance</div>
                <div className="space-y-3">
                  {((data as any)?.offers || []).map((o: any) => (
                    <div key={o.type} className="flex items-center justify-between">
                      <div className="text-martinique dark:text-white">{o.type}</div>
                      <div className="font-medium text-martinique dark:text-white">{o.accepted}</div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </>
        )}
      </div>
  )
}

export default AdminRetention
